import BasicCard from "@components/ArticleCard/BasicCard";
import { CardVariations } from "@components/ArticleCard/types";
import { useWindowSize } from "@hooks/useWindowSize";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { SanitizedSectionArticle } from "@typings/Cue";
import { cn, getFormattedUrlPath } from "@util/helpers";

type HighlightsLeadProps = {
  /** The classname of the component. */
  className?: string;
  /** The article to be displayed as the lead highlight. */
  article: SanitizedSectionArticle;
};

/** This is the first article in the highlights component.
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79055&m=dev
 */
const HighlightsLead = ({
  className,
  article: { id, title, urlPath, media, kicker, blurb, paywall },
}: HighlightsLeadProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const OktaUserInfo = useOKTAUserStore((store) => store.userInfo);
  const isSubscriber = OktaUserInfo?.usertype === OKTAUserTypeEnum.SUBSCRIBER;

  const link = getFormattedUrlPath(urlPath, [
    { key: "ref", value: "btsg60-highlights" },
  ]);
  const variant: CardVariations = {
    image: {
      position: "left",
      width: cn("w-1/2"),
    },
    content: {
      width: cn("w-1/2"),
      extraClass: cn(
        "bg-gray-850 text-center py-4 px-6 lg:bg-transparent lg:text-left lg:p-0 lg:pl-4"
      ),
      blurb: {
        extraClass: cn("font-public-sans text-base mt-2"),
      },
    },
    title: {
      color: cn("text-gray-850"),
      size: cn("text-4xl"),
      extraClass: cn(
        "text-white font-semibold lg:text-gray-850 leading-[1.8125rem]"
      ),
    },
    kicker: {
      color: cn("text-gray-515"),
    },
  };

  return (
    <BasicCard
      id={id}
      title={title}
      slug={link}
      media={isScreenLG ? media : undefined}
      kicker={kicker?.fields?.[0]?.value}
      blurb={blurb}
      paywall={isSubscriber ? paywall.contentAccess === "1" : undefined}
      variations={variant}
      rootClassName={cn(
        "relative after:absolute after:-bottom-4 after:w-full after:h-[1px] after:bg-gray-175",
        className
      )}
    />
  );
};

export default HighlightsLead;
